import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/Admin/AdminLayout';
import DataTable from '../../components/Admin/DataTable';
import blogService from '../../services/blogService';
import { useAuth } from '../../contexts/AuthContext';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalBlogs: 0,
    publishedBlogs: 0,
    draftBlogs: 0,
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { admin } = useAuth();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await blogService.getAllBlogs({ limit: 100, showAll: true });

      if (response.success && response.data) {
        const blogs = Array.isArray(response.data) ? response.data : [];
        const published = blogs.filter(blog => blog.published === 1 || blog.is_published === true).length;
        const drafts = blogs.filter(blog => blog.published === 0 || blog.is_published === false).length;

        setStats({
          totalBlogs: blogs.length,
          publishedBlogs: published,
          draftBlogs: drafts,
        });

        setRecentBlogs(blogs.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns = [
    {
      header: 'Title',
      accessor: 'title',
      render: (item) => (
        <div>
          <div className="font-medium text-white">{item.title}</div>
          <div className="text-xs text-gray-500 line-clamp-1">{item.description}</div>
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (item) => (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-900/30 text-blue-400 border border-blue-800/50">
          {item.category}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'published',
      render: (item) => {
        const isPublished = item.published === 1 || item.is_published === true;
        return (
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isPublished
            ? 'bg-green-900/30 text-green-400 border border-green-800/50'
            : 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/50'
            }`}>
            {isPublished ? 'Published' : 'Draft'}
          </span>
        );
      }
    },
    {
      header: 'Date',
      accessor: 'created_at',
      render: (item) => <span className="text-gray-400">{formatDate(item.created_at)}</span>
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 shadow-2xl">
          <div className="relative z-10">
            <h1 className="text-3xl font-bold text-white mb-2">Welcome Back, {admin?.name || 'Admin'}</h1>
            <p className="text-blue-100 max-w-2xl">
              Here is what is happening with your blog content today. You have {stats.draftBlogs} drafts pending review.
            </p>
          </div>
          {/* Decorative Circle */}
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatsCard
            title="Total Blogs"
            value={stats.totalBlogs}
            icon={<DocumentIcon />}
            color="blue"
          />
          <StatsCard
            title="Published"
            value={stats.publishedBlogs}
            icon={<CheckCircleIcon />}
            color="green"
          />
          <StatsCard
            title="Drafts"
            value={stats.draftBlogs}
            icon={<PencilIcon />}
            color="yellow"
          />
        </div>

        {/* Quick Actions & Recent Blogs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Blogs Table */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Recent Blogs</h2>
              <Link to="/admin/blogs" className="text-sm text-blue-400 hover:text-blue-300">
                View All
              </Link>
            </div>
            <DataTable
              columns={columns}
              data={recentBlogs}
              isLoading={loading}
              onEdit={true}
            />
          </div>

          {/* Quick Actions */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white">Quick Actions</h2>
            <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-xl space-y-4">
              <Link
                to="/admin/blogs/new"
                className="block w-full"
              >
                <div className="flex items-center p-4 rounded-lg bg-blue-600/10 border border-blue-500/20 hover:bg-blue-600/20 transition-all group cursor-pointer">
                  <div className="p-2 bg-blue-600 rounded-lg text-white mr-4 shadow-lg group-hover:scale-110 transition-transform">
                    <PlusIcon />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Create New Post</h3>
                    <p className="text-xs text-blue-200/70">Write and publish new content</p>
                  </div>
                </div>
              </Link>

              <Link
                to="/admin/blogs"
                className="block w-full"
              >
                <div className="flex items-center p-4 rounded-lg bg-purple-600/10 border border-purple-500/20 hover:bg-purple-600/20 transition-all group cursor-pointer">
                  <div className="p-2 bg-purple-600 rounded-lg text-white mr-4 shadow-lg group-hover:scale-110 transition-transform">
                    <ListIcon />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Manage Content</h3>
                    <p className="text-xs text-purple-200/70">Edit or delete existing posts</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// Sub-components for cleaner code
function StatsCard({ title, value, icon, color }) {
  const colorStyles = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    yellow: "bg-yellow-500"
  };

  return (
    <div className="bg-[#1a1025] rounded-xl border border-white/5 p-6 shadow-xl relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
      <div className="flex justify-between items-start relative z-10">
        <div>
          <p className="text-sm font-medium text-gray-400 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-white">{value}</h3>
        </div>
        <div className={`p-3 rounded-xl ${colorStyles[color]} bg-opacity-10 text-${color}-400`}>
          {icon}
        </div>
      </div>
      {/* Glow effect */}
      <div className={`absolute -bottom-4 -right-4 w-24 h-24 ${colorStyles[color]} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity`}></div>
    </div>
  );
}

// Icons
const DocumentIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
);
const CheckCircleIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const PencilIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
);
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
);
const ListIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
);

